"use client";

import { FormattedMessage } from "react-intl";
import { SpotTile, type SpotName, type SpotTone } from "../Spot/SpotIcon";

/** One gain for the organisation: its copy (`profile.organization.gains.<id>.title|desc`) and icon. */
type Gain = { id: string; icon: SpotName; tone: SpotTone };

/**
 * The buyer's rollout objections, lifted in the order they are raised (from the sales
 * argument): will the team learn it (five minutes, pillar 1), must we retype everything
 * (Notion and Todoist import), how does a newcomer get up to speed (one email, the /guide
 * team step), will people actually use it (the individual served so the team moves).
 * Tones avoid the three cards' own (green, pink, purple).
 */
const GAINS: Gain[] = [
  { id: "fiveMinutes", icon: "productivite", tone: "blue" },
  { id: "import", icon: "import", tone: "navy" },
  { id: "newcomer", icon: "team", tone: "sky" },
  { id: "wanted", icon: "day", tone: "amber" },
];

/**
 * The organisation tab's business case, set after the three animated arguments: a short
 * heading and lead beside four gains, each an illustrated tile, a bold title and one line.
 * Static on purpose: the cards above already carry the motion.
 */
export function TeamGains() {
  return (
    <section
      aria-labelledby="profile-gains-title"
      className="mt-12 rounded-[32px] bg-blue-ulight/70 p-6 ring-1 ring-blue-light sm:mt-16 sm:rounded-[36px] sm:p-10 xl:grid xl:grid-cols-[minmax(0,4fr)_minmax(0,8fr)] xl:items-center xl:gap-14 xl:p-12"
    >
      <div>
        <h3
          id="profile-gains-title"
          className="text-balance text-2xl font-bold leading-snug tracking-[-0.015em] text-title-blue sm:text-3xl"
        >
          <FormattedMessage id="profile.organization.gains.title" />
        </h3>
        <p className="mt-3 max-w-xl text-balance text-base leading-relaxed text-text-main sm:text-lg">
          <FormattedMessage id="profile.organization.gains.lead" />
        </p>
      </div>

      <ul className="mt-8 grid gap-6 sm:grid-cols-2 sm:gap-x-8 sm:gap-y-8 xl:mt-0">
        {GAINS.map((g) => (
          <li key={g.id} className="flex items-start gap-4">
            <SpotTile name={g.icon} tone={g.tone} onTint className="h-12 w-12 rounded-2xl p-2 ring-1 ring-blue-light" />
            <div className="min-w-0">
              <h4 className="text-balance text-lg font-bold leading-snug text-title-blue">
                <FormattedMessage id={`profile.organization.gains.${g.id}.title`} />
              </h4>
              <p className="mt-1 text-[15px] leading-relaxed text-text-main">
                <FormattedMessage id={`profile.organization.gains.${g.id}.desc`} />
              </p>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
